import React from "react";

import { segmentArcAngles, segmentCount } from "../../chartGeometry";
import { ConfigData } from "../../config";
import { hashFraction } from "../../hash";
import { Item } from "../../model";

/**
 * The radar itself, drawn small enough to be read as a picture.
 *
 * It is the real thing and not a decoration: the segments come from the radar's
 * own `config.json` (four for Tech, six for AI), every blip of the selected
 * edition is plotted, and positions are derived from `hashFraction` so the
 * miniature is identical on every render — the same property the chart relies on.
 *
 * Angles are computed in the arc frame used by `chartGeometry`: 0 is 12 o'clock
 * and angles grow clockwise, hence `x = sin a`, `y = -cos a`.
 */

const SIZE = 300;
const CENTRE = SIZE / 2;
/** Outer radius of each ring, from the innermost. Matches the design's preview. */
const RING_RADII = [32, 68, 104, 140];
const RING_COLOURS = [
  "var(--ring-adopt)",
  "var(--ring-trial)",
  "var(--ring-assess)",
  "var(--ring-hold)",
];
/** Keeps blips off the ring borders. */
const RING_PADDING = 3.5;
/** Keeps blips off the segment borders, as a fraction of the segment's width. */
const SEGMENT_INSET = 0.12;

type Dot = { x: number; y: number; colour: string };

const dotsOf = (items: Item[], config: ConfigData): Dot[] => {
  const segments = segmentCount(config);

  return items.reduce<Dot[]>((dots, item) => {
    const quadrant = config.quadrantsMap[item.quadrant];
    const ring = config.rings.indexOf(item.ring);
    if (!quadrant || ring < 0) {
      return dots;
    }

    const [start, end] = segmentArcAngles(quadrant.position, segments);
    const angle =
      start +
      (SEGMENT_INSET +
        hashFraction(`${item.name}:angle:0`) * (1 - 2 * SEGMENT_INSET)) *
        (end - start);

    const inner = ring === 0 ? 0 : RING_RADII[ring - 1];
    const outer = RING_RADII[ring] ?? RING_RADII[RING_RADII.length - 1];
    const radius =
      inner +
      RING_PADDING +
      hashFraction(`${item.name}:radius:0`) *
        Math.max(0, outer - inner - 2 * RING_PADDING);

    dots.push({
      x: CENTRE + Math.sin(angle) * radius,
      y: CENTRE - Math.cos(angle) * radius,
      colour: RING_COLOURS[Math.min(ring, RING_COLOURS.length - 1)],
    });
    return dots;
  }, []);
};

const separators = (config: ConfigData) => {
  const segments = segmentCount(config);
  const outer = RING_RADII[RING_RADII.length - 1] + 6;

  return Array.from({ length: segments }, (_, index) => {
    const angle = (2 * Math.PI * index) / segments;
    return {
      x: CENTRE + Math.sin(angle) * outer,
      y: CENTRE - Math.cos(angle) * outer,
    };
  });
};

export default function RadarMiniature({
  items,
  config,
  className,
}: {
  items: Item[];
  config: ConfigData;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      aria-hidden="true"
      focusable="false"
    >
      {RING_RADII.map((radius, index) => (
        <circle
          key={radius}
          cx={CENTRE}
          cy={CENTRE}
          r={radius}
          fill="none"
          stroke={index === 0 ? "var(--border-strong)" : "var(--border)"}
        />
      ))}
      {separators(config).map((point, index) => (
        <line
          key={index}
          x1={CENTRE}
          y1={CENTRE}
          x2={point.x}
          y2={point.y}
          stroke="var(--surface-3)"
        />
      ))}
      {dotsOf(items, config).map((dot, index) => (
        <circle
          key={index}
          cx={dot.x}
          cy={dot.y}
          r={2.2}
          fill={dot.colour}
          opacity={0.9}
        />
      ))}
    </svg>
  );
}
